import {
  MAX_TIMING_SAMPLES,
  RUN_TIMING_VERSION,
  type RunTimingReport,
  type RunTimingSample,
} from '@shared/antiCheat/runTiming';

/**
 * Whether a DOM event came from a real user gesture. Genuine input
 * (hardware key/mouse/touch) sets `isTrusted = true`; anything created
 * by script via `dispatchEvent` / `new KeyboardEvent(...)` — i.e. an
 * input-replay bot — sets it `false`. The `!== false` shape treats a
 * legacy/exotic `undefined` as trusted so we never false-flag a real
 * but unusual browser.
 */
export function isTrustedEvent(e: Event): boolean {
  return e.isTrusted !== false;
}

/**
 * How often the self-driven heartbeat samples the clocks (ms). Discrete
 * input events alone are too sparse in hold-heavy modes (Waves/Jump can
 * be a single held button for a whole song), so a steady ~6 Hz heartbeat
 * keeps the beat-vs-wall stream dense enough to median across in EVERY
 * mode. Jitter in the interval is irrelevant — we only need points
 * spread across real time.
 */
const HEARTBEAT_INTERVAL_MS = 150;

/**
 * Captures the anti-cheat timing stream for one run. Owned by a game
 * session for its lifetime. Two capture paths feed one sample buffer:
 *   - **Input events** (capture-phase `keydown` / `pointerdown` on
 *     `window`): also tally `event.isTrusted` so synthetic-event bots
 *     are countable server-side. Covers mouse + touch + pen (pointer
 *     events) and keyboard.
 *   - **Heartbeat** (`setInterval`): a clock sample independent of input
 *     frequency, so sparse-input modes still produce a usable stream.
 *
 * Both paths only record while the run's clock is genuinely running and
 * past beat 0 (lead-in taps carry negative beats and aren't gameplay).
 * Samples are adaptively down-sampled to {@link MAX_TIMING_SAMPLES} so a
 * long infinite-mode run can't bloat the submit payload.
 *
 * This is the client half of the anti-cheat timing system; the server
 * verifies the emitted {@link RunTimingReport} via
 * `verifyRunTiming` (`@shared/antiCheat/runTiming`).
 */
export interface RunTimelineConfig {
  /** Live audio-clock beat (this loop's space). Same source the input sources stamp with. */
  getBeatAtNow: () => number;
  /** True only while the run is actively running (not idle / lead-in / paused / over). */
  isClockRunning: () => boolean;
  /** Event target for input listeners. Defaults to `window`. */
  target?: EventTarget;
}

export class RunTimeline {
  private readonly cfg: RunTimelineConfig;
  private readonly target: EventTarget;
  private samples: RunTimingSample[] = [];
  private currentLoop = 0;
  private totalInputs = 0;
  private untrustedInputs = 0;
  private startWallMs: number | null = null;
  private startMonoMs: number | null = null;
  /** Keep-every-Nth decimation factor; doubles each time we hit the cap. */
  private stride = 1;
  /** Count of recorded candidates, for stride decimation. */
  private recordSeq = 0;
  private heartbeat: ReturnType<typeof setInterval> | null = null;
  private attached = false;

  constructor(cfg: RunTimelineConfig) {
    this.cfg = cfg;
    this.target = cfg.target ?? window;
  }

  /** Begin capturing. Idempotent. */
  attach(): void {
    if (this.attached) {
      return;
    }
    this.attached = true;
    // Capture phase so we observe the event regardless of whether a
    // gameplay listener later calls stopPropagation; passive so we never
    // interfere with the input sources' own preventDefault().
    this.target.addEventListener('keydown', this.onInput, { capture: true, passive: true });
    this.target.addEventListener('pointerdown', this.onInput, { capture: true, passive: true });
    this.heartbeat = setInterval(this.onHeartbeat, HEARTBEAT_INTERVAL_MS);
  }

  /** Stop capturing and release listeners/timers. Idempotent. */
  detach(): void {
    if (!this.attached) {
      return;
    }
    this.attached = false;
    this.target.removeEventListener('keydown', this.onInput, { capture: true });
    this.target.removeEventListener('pointerdown', this.onInput, { capture: true });
    if (this.heartbeat !== null) {
      clearInterval(this.heartbeat);
      this.heartbeat = null;
    }
  }

  /**
   * Advance the loop index. Called by the session whenever it opens a
   * fresh per-loop capture bucket — the audio clock resets each loop, so
   * the verifier only compares beats within a single loop.
   */
  startLoop(loopIndex: number): void {
    this.currentLoop = loopIndex;
  }

  private onInput = (e: Event): void => {
    if (!this.cfg.isClockRunning()) {
      return;
    }
    const beat = this.cfg.getBeatAtNow();
    if (!Number.isFinite(beat) || beat < 0) {
      return;
    }
    this.totalInputs += 1;
    if (!isTrustedEvent(e)) {
      this.untrustedInputs += 1;
    }
    this.pushSample(beat);
  };

  private onHeartbeat = (): void => {
    if (!this.cfg.isClockRunning()) {
      return;
    }
    const beat = this.cfg.getBeatAtNow();
    if (!Number.isFinite(beat) || beat < 0) {
      return;
    }
    this.pushSample(beat);
  };

  private pushSample(beat: number): void {
    const wallMs = Date.now();
    const monoMs = performance.now();
    if (this.startWallMs === null) {
      this.startWallMs = wallMs;
      this.startMonoMs = monoMs;
    }
    // Adaptive uniform decimation: keep every `stride`-th candidate, and
    // when the buffer would exceed the cap, drop every other kept sample
    // and double the stride. Keeps coverage spread evenly across the run
    // while bounding memory + payload size.
    if (this.recordSeq % this.stride === 0) {
      this.samples.push({ loop: this.currentLoop, beat, wallMs, monoMs });
      if (this.samples.length >= MAX_TIMING_SAMPLES * 2) {
        this.samples = this.samples.filter((_, i) => i % 2 === 0);
        this.stride *= 2;
      }
    }
    this.recordSeq += 1;
  }

  /**
   * Serialise the captured stream for submission. Returns `null` when
   * nothing was captured (e.g. a replay-preview run, or a run abandoned
   * during lead-in) so callers can omit the field entirely.
   */
  toReport(): RunTimingReport | null {
    if (this.startWallMs === null || this.startMonoMs === null || this.samples.length === 0) {
      return null;
    }
    return {
      v: RUN_TIMING_VERSION,
      startWallMs: this.startWallMs,
      startMonoMs: this.startMonoMs,
      totalInputs: this.totalInputs,
      untrustedInputs: this.untrustedInputs,
      samples: this.samples.slice(),
    };
  }
}
